import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ClipboardList, Loader2, X, MapPin, Phone, ExternalLink, AlertTriangle, Headset } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TONE = {
  green: "bg-emerald-500", blue: "bg-sky-500", amber: "bg-amber-500",
  red: "bg-rose-500", gray: "bg-gray-400",
};

export const StatusPill = ({ label, tone }) => (
  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700 whitespace-nowrap">
    <span className={`w-2.5 h-2.5 rounded-full ${TONE[tone] || TONE.blue}`} />
    {label}
  </span>
);

const money = (v) => (v || v === 0 ? `₹${Math.round(v).toLocaleString("en-IN")}` : "—");
const when = (s) => (s ? new Date(s).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "");

function OrderDetail({ id, seniorName, onClose }) {
  const [order, setOrder] = useState(null);

  useEffect(() => {
    axios.get(`${API}/family/senior-order/${encodeURIComponent(id)}`)
      .then((r) => setOrder(r.data.order))
      .catch((e) => { toast.error(e.response?.data?.detail || "Could not load this order."); onClose(); });
  }, [id]);

  return (
    <div className="fixed inset-0 z-[70] flex items-end md:items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="order-detail-modal">
      <div className="absolute inset-0" onClick={onClose} />
      <div className="relative bg-white rounded-t-[28px] md:rounded-[24px] w-full md:max-w-lg max-h-[88vh] overflow-y-auto p-6 shadow-2xl">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100" data-testid="order-detail-close">
          <X className="w-5 h-5 text-gray-500" />
        </button>
        {!order ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#2D2766]" /></div>
        ) : (
          <div className="space-y-5">
            <div>
              <p className="text-2xl font-semibold text-[#1A172E]" data-testid="order-detail-title">{order.icon} {order.title}</p>
              <div className="flex items-center gap-3 mt-1.5">
                <StatusPill label={order.status_label} tone={order.status_tone} />
                <span className="text-sm text-gray-400">Placed by {order.placed_by}</span>
              </div>
            </div>

            {order.needs_attention && (
              <div className="rounded-xl bg-rose-50 border border-rose-200 p-3.5" data-testid="order-detail-attention">
                <p className="text-sm font-semibold text-rose-700 flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4" /> This order needs attention
                </p>
                <a href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER || "919205070300"}`} target="_blank" rel="noopener noreferrer"
                   className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#2D2766] hover:underline"
                   data-testid="order-detail-talk-btn">
                  <Headset className="w-4 h-4" /> Talk to Elderra
                </a>
              </div>
            )}

            {order.items?.length > 0 && (
              <div className="rounded-2xl border border-gray-200 divide-y" data-testid="order-detail-items">
                {order.items.map((it, i) => (
                  <div key={i} className="flex items-start justify-between gap-3 px-4 py-3">
                    <div>
                      <p className="text-base text-[#1A172E]">{it.name}</p>
                      <p className="text-sm text-gray-400">Qty {it.qty}{it.price ? ` × ${money(it.price)}` : ""}</p>
                    </div>
                    <p className="text-base font-semibold text-[#1A172E] whitespace-nowrap">
                      {it.price ? money(it.price * it.qty) : "—"}
                    </p>
                  </div>
                ))}
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-b-2xl">
                  <p className="font-semibold text-[#1A172E]">Total</p>
                  <p className="font-bold text-[#2D2766] text-lg" data-testid="order-detail-total">{money(order.amount)}</p>
                </div>
              </div>
            )}

            {!order.items?.length && order.lines?.length > 0 && (
              <ul className="space-y-1.5 text-base text-[#333]" data-testid="order-detail-lines">
                {order.lines.map((l, i) => <li key={i}>• {l}</li>)}
              </ul>
            )}

            <div className="space-y-2.5 text-base text-[#333]">
              {order.address && <p className="flex gap-2"><MapPin className="w-4 h-4 text-gray-400 mt-1 shrink-0" /> {order.address}</p>}
              {order.payment && <p className="text-sm text-gray-500 capitalize">Payment: {order.payment === "online" ? "Paid online" : "Cash on delivery"}</p>}
              {order.rider_name && (
                <p className="flex items-center gap-2" data-testid="order-detail-rider">
                  <Phone className="w-4 h-4 text-gray-400" /> {order.rider_name}
                  {order.rider_phone && <a href={`tel:+${order.rider_phone}`} className="text-[#2D2766] font-semibold hover:underline">+{order.rider_phone}</a>}
                </p>
              )}
              {order.tracking_url && (
                <a href={order.tracking_url} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#2D2766] hover:underline" data-testid="order-detail-track">
                  <ExternalLink className="w-4 h-4" /> Track live
                </a>
              )}
            </div>

            {order.timeline?.length > 0 && (
              <div data-testid="order-detail-timeline">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2.5">What happened</p>
                <ol className="space-y-2.5 border-l-2 border-gray-100 pl-4">
                  {order.timeline.map((e, i) => (
                    <li key={i} className="relative">
                      <span className="absolute -left-[21px] top-1.5 w-2.5 h-2.5 rounded-full bg-[#2D2766]" />
                      <p className="text-base text-[#1A172E]">{e.label}</p>
                      <p className="text-xs text-gray-400">{when(e.at)}</p>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function OrderFeed({ orders, loading, seniorName }) {
  const [openId, setOpenId] = useState(null);

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#2D2766]" /></div>;
  }
  if (!orders.length) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center text-gray-400" data-testid="senior-orders-empty">
        <ClipboardList className="w-10 h-10 mx-auto mb-2" />
        <p>No orders yet.</p>
      </div>
    );
  }
  return (
    <>
      <div className="bg-white rounded-2xl border border-gray-200 divide-y" data-testid="senior-orders-list">
        {orders.map((o) => (
          <button key={o.id} onClick={() => setOpenId(o.id)}
            className="w-full text-left p-4 flex items-start justify-between gap-3 hover:bg-gray-50 transition-colors"
            data-testid={`senior-order-${o.id}`}>
            <div className="min-w-0">
              <p className="font-semibold text-[#1A172E]">{o.icon} {o.title}</p>
              <p className="text-sm text-gray-500 truncate">{o.summary || "—"}</p>
              <p className="text-xs text-gray-400 mt-1">
                {when(o.created_at)} · {o.source} · by {o.placed_by}
              </p>
            </div>
            <div className="text-right shrink-0 space-y-1.5">
              <p className="font-semibold text-[#1A172E]">{money(o.amount)}</p>
              <StatusPill label={o.status_label} tone={o.status_tone} />
            </div>
          </button>
        ))}
      </div>
      {openId && <OrderDetail id={openId} seniorName={seniorName} onClose={() => setOpenId(null)} />}
    </>
  );
}
